/**
 * @module app.um
 */

import { autoinject } from 'aurelia-framework';
import { ComponentBase, IForm, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { Toaster } from 'app/shared/lib/toaster';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { EssPagerCustomElement, PageChangeEvent } from 'app/shared/elements/ess-pager';
import { EventAggregator } from 'aurelia-event-aggregator';
import { MenuRefreshEvent } from 'app/shared/lib/events';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { localSort } from 'app/shared/lib/sorting';
import { I18N } from 'aurelia-i18n';
import { FaxService } from 'app/fm/services/fax-service';
import { validate } from 'app/shared/elements/ess-rule';
import { IPopup } from 'app/shared/elements/popup-base';
import { deepClone } from 'app/shared/lib/utilities';
import * as FM from 'app/fm/models/essette-api';
import { confirmation } from 'app/shared/elements/ess-confirm';

interface CustomFaxQueue extends FM.FaxQueue {
    actionsDisplay?: string | null;
    actionsList?: string[] | null;
}

@autoinject()
export class ManageFaxQueuesPage extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected faxQueues: CustomFaxQueue[] = [];
    protected editedFaxQueue: FM.FaxQueue = {};
    protected faxQueueActions: FM.FaxQueueAction[] = [];

    protected validate = validate;
    protected isValid = false;

    private apiSorting = new Sorting();
    private localSorting = new Sorting();
    private localSortProperties: string[] = ['actionsDisplay'];

    protected resultCount = 0;
    protected page = 0;
    protected pageSize = 25;
    protected includeInactive = false;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $modal!: IPopup;
    protected $form!: IForm;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: FaxService,
        private readonly events: EventAggregator,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);
    }

    protected async activate(): Promise<void> {
        await this.loadFaxQueueActions();
        await this.reload();
    }

    ////****************** Events ***************************************************////
    public async reload(): Promise<void> {
        await this.loadFaxQueues();
        // if empty last page is loaded, calculate and change to actual last page
        if(!this.faxQueues.length && this.resultCount > 0 && this.page > 0) {
            const lastPage = Math.max(Math.ceil(this.resultCount / this.pageSize) - 1, 0);
            this.$pager.changePage(lastPage);
        }
    }

    /** Handles the pager's page-change event */
    protected async pageChange(event: PageChangeEvent): Promise<void> {

        // prevent the default page change behavior
        const { page } = event.detail;
        event.preventDefault();

        // manually update the page
        this.$pager.updatePage(page);

        // and after the new set of results returns
        this.page = page;
        await this.reload();
    }

    /** Handles the grid's sort event */
    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;

        //If LocalSort
        if(this.localSortProperties.indexOf(property) > -1) {
            const { localSorting } = this;
            const faxQueues: CustomFaxQueue[] = <CustomFaxQueue[]>this.faxQueues;
            if(direction === null) {
                this.localSorting.remove(property);
                await this.reload();
            } else {
                this.localSorting.add({
                    reference: property,
                    direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
                });
                localSort(faxQueues, localSorting);
            }
        }
        // Else If API Sort
        else {
            const { apiSorting } = this;
            if(direction === null) {
                apiSorting.remove(property);
            } else {
                apiSorting.add({
                    reference: property,
                    direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
                });
            }
            await this.reload();
        }
    }

    protected handleFormSuccess(): void {
        this.closeModal();
        this.refreshMainMenu();
        this.reload();
    }

    ////****************** Functions ************************************************////
    // Refresh navigation menu to display fax queues
    protected refreshMainMenu(): void {
        this.events.publish(new MenuRefreshEvent);
        this.accountContext.clear();
    }

    protected closeModal(): void {
        if(this.$form.reset) this.$form.reset();
        this.$modal.close();
    }

    protected saveFaxQueue(faxQueue: FM.FaxQueue): void {
        this.editedFaxQueue = deepClone(faxQueue);
        this.$modal.open();
    }

    // Set Custom Fax Queue Details
    private setFaxQueueDetails(faxQueues: CustomFaxQueue[]): void {
        faxQueues?.forEach(queue => {
            // Configure Actions column data
            if(!queue?.actions || !queue.actions.length) {
                // if no actions, display nothing at all
                queue.actionsDisplay = '';
            } else if(queue.actions.length === 1) {
                // if only one action, display that action
                queue.actionsDisplay = queue.actions[0].description;
            } else if(queue.actions.length >= this.faxQueueActions.length) {
                // if all actions (or somehow more), display "All Actions"
                queue.actionsDisplay = this.i18n.tr('ui:labels.all', { what: this.i18n.tr('fm:faxQueue.action.$', { count: 2 }) });
            } else {
                // otherwise, display "n Actions" and generate a list for the tooltip
                queue.actionsDisplay = `${queue.actions.length} ` + this.i18n.tr('fm:faxQueue.action.$', { count: 2 });
                queue.actionsList = queue.actions.flatMap(a => a.description ?? '')
                    .sort((a, b) => a.localeCompare(b))
            }
        });
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadFaxQueues(): Promise<void> {
        // Default Sort enforced if no other column sorts specified
        if(!this.apiSorting.entries.length) {
            this.apiSorting.entries.push({
                reference: 'description',
                direction: SortDirection.Ascending
            });
        }

        const options: Partial<IQueryLoadOptions> = {
            page: this.page,
            top: this.pageSize,
            sorting: this.apiSorting,
            includeInactive: this.includeInactive
        };

        try {
            this.state.beginLoad();

            const faxQueuesResponse = await this.service.loadFaxQueues(options, ['actions', 'actions.parameters', 'parameters']);

            this.setFaxQueueDetails(<CustomFaxQueue[]>faxQueuesResponse.results);
            this.faxQueues = faxQueuesResponse.results;
            this.resultCount = faxQueuesResponse.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadFaxQueueActions(): Promise<void> {
        try {
            this.state.beginLoad();
            const response = await this.service.loadFaxQueueActions({ all: true });
            this.faxQueueActions = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async activateFaxQueue(faxQueue: FM.FaxQueue): Promise<void> {
        if(!faxQueue.id) return;
        if(faxQueue.isActive) {
            const result = await confirmation.showDeactivate('fm:faxQueue.$long', 1, false);
            if(result.dialogResponse === confirmation.options.CANCEL) { return; }

            if(!(result.dialogResponse === confirmation.options.OK)) {
                return;
            }
        }
        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.activateFaxQueue(faxQueue.id, !faxQueue.isActive);
            isSuccessful = response.isSuccessful;
            this.refreshMainMenu();
            await this.reload();
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.activate('fm:faxQueue.$long', !faxQueue.isActive, isSuccessful);
            this.state.endSave();
        }
    }
}
